import React from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface StreamingPassage {
  number: number;
  title: string;
  content: string;
  genre?: string;
  isComplete: boolean;
}

interface StreamingAssessmentDisplayProps {
  passages: StreamingPassage[];
  questionsContent: string;
  answerKeyContent: string;
  status: string;
  isStreaming: boolean;
  isQuestionsComplete: boolean;
}

const StreamingAssessmentDisplay: React.FC<StreamingAssessmentDisplayProps> = ({
  passages,
  questionsContent,
  answerKeyContent,
  status,
  isStreaming,
  isQuestionsComplete
}) => {
  
  return (
    <Paper elevation={3} sx={{ p: 4, mb: 3 }}>

      {/* Streaming Passages */}
      {passages.map((passage) => (
        <Box 
          key={passage.number} 
          sx={{ 
            mb: 6,
            maxWidth: '900px',
            margin: '0 auto 48px auto',
            px: 4,
            position: 'relative'
          }}
        >
          {/* Passage Title */}
          {passage.title && (
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                textAlign: 'center',
                marginBottom: '2em',
                color: '#2c5282',
                fontWeight: 'bold',
                fontSize: '1.5rem'
              }}
            >
              {passage.title}
            </Typography>
          )}

          {/* Passage Content */}
          <Box sx={{
            '& p': {
              textAlign: passage.genre === 'Poetry' ? 'center' : 'left',
              lineHeight: '1.8',
              marginBottom: '1em',
              fontSize: '1rem'
            },
            '& h1': {
              display: 'none' // Hide h1 from markdown since we show title separately
            },
            '& h2, & h3': {
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#2c5282',
              marginBottom: '1em'
            }
          }}>
            {passage.content && (
              <>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {passage.content}
                </ReactMarkdown>
                
                {/* Blinking cursor if still streaming */}
                {!passage.isComplete && (
                  <Box 
                    component="span" 
                    sx={{ 
                      display: 'inline-block',
                      width: '3px',
                      height: '20px',
                      backgroundColor: '#1976d2',
                      marginLeft: '4px',
                      animation: 'blink 1s infinite',
                      '@keyframes blink': {
                        '0%, 50%': { opacity: 1 },
                        '51%, 100%': { opacity: 0 }
                      }
                    }} 
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      ))}

      {/* Streaming Questions */}
      {questionsContent && (
        <Box sx={{ 
          mt: 6,
          maxWidth: '900px',
          margin: '48px auto 0 auto',
          px: 4,
          borderTop: '2px solid #eee',
          pt: 4
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              textAlign: 'center',
              mb: 3,
              color: '#2c5282',
              fontWeight: 'bold',
              fontSize: '1.25rem'
            }}
          >
            Questions
          </Typography>
          
          {/* Show loading state while questions are being generated */}
          {!isQuestionsComplete ? (
            <Box sx={{
              textAlign: 'center',
              py: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <CircularProgress size={40} />
              <Typography variant="body1" color="text.secondary">
                Generating questions...
              </Typography>
            </Box>
          ) : (
            /* Show parsed questions when complete */
            <Box sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: '1rem',
              lineHeight: 1.6,
              '& p': {
                marginBottom: '1em'
              }
            }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {questionsContent}
              </ReactMarkdown>
            </Box>
          )}
        </Box>
      )}

      {/* Streaming Answer Key */}
      {answerKeyContent && (
        <Box sx={{ 
          mt: 6,
          pt: 4,
          borderTop: '2px solid #2c5282',
          maxWidth: '900px',
          margin: '48px auto 0 auto',
          px: 4
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              textAlign: 'center',
              mb: 3,
              color: '#2c5282',
              fontWeight: 'bold',
              fontSize: '1.25rem'
            }}
          >
            Answer Key
          </Typography>
          
          <Box sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
            fontSize: '1rem',
            lineHeight: 1.6,
            '& p': {
              marginBottom: '1em'
            },
            '& strong': {
              fontWeight: 'bold',
              color: '#2c5282'
            }
          }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {answerKeyContent}
            </ReactMarkdown>
            
            {/* Blinking cursor if still streaming answer key */}
            {isStreaming && isQuestionsComplete && (
              <Box 
                component="span" 
                sx={{ 
                  display: 'inline-block',
                  width: '3px',
                  height: '20px',
                  backgroundColor: '#1976d2',
                  marginLeft: '4px',
                  animation: 'blink 1s infinite',
                  '@keyframes blink': {
                    '0%, 50%': { opacity: 1 },
                    '51%, 100%': { opacity: 0 }
                  }
                }} 
              />
            )}
          </Box>
        </Box>
      )}

      {/* Empty state */}
      {passages.length === 0 && !questionsContent && !answerKeyContent && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Generate an assessment to see content here
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default StreamingAssessmentDisplay;

